<template>
  <div class="pr-root" @click="fecharDropdowns">
    <div class="pr-header">
      <div class="pr-header-icon">
        <fa-icon icon="filter" />
      </div>
      <div>
        <h3 class="pr-title">Regras Condicionais</h3>
        <p class="pr-desc">Sobrescreva valores de variáveis antes do cálculo com base em condições.</p>
      </div>
    </div>

    <div v-if="!regras.length" class="pr-empty">
      <div class="pr-empty-visual">
        <div class="pr-empty-pill pr-pill-if">SE</div>
        <div class="pr-empty-arrow">
          <fa-icon icon="chevron-right" />
        </div>
        <div class="pr-empty-pill pr-pill-then">ENTÃO</div>
      </div>
      <p class="pr-empty-text">Nenhuma regra configurada</p>
      <p class="pr-empty-sub">Adicione regras para alterar valores dinamicamente</p>
      <button class="pr-btn-add" @click="adicionar">
        <fa-icon icon="plus" /> Nova Regra
      </button>
    </div>

    <div v-else class="pr-lista">
      <div
        v-for="(regra, idx) in regras"
        :key="idx"
        class="pr-card"
        :class="{ 'pr-card-off': !regra.ativa, 'pr-card-dup': duplicatas.has(idx) }"
      >
        <div class="pr-card-top">
          <div class="pr-card-badge">
            <fa-icon icon="exchange-alt" />
            Regra {{ idx + 1 }}
            <span v-if="duplicatas.has(idx)" class="pr-dup-warn">
              <fa-icon icon="exclamation-triangle" /> Duplicada
            </span>
          </div>
          <div class="pr-card-actions">
            <button
              class="pr-act-btn"
              :class="regra.ativa ? 'pr-act-on' : 'pr-act-off'"
              :title="regra.ativa ? 'Desativar' : 'Ativar'"
              @click="toggleAtiva(idx)"
            >
              <fa-icon :icon="regra.ativa ? 'eye' : 'eye-slash'" />
            </button>
            <button class="pr-act-btn pr-act-del" @click="remover(idx)" title="Remover">
              <fa-icon icon="times" />
            </button>
          </div>
        </div>

        <div class="pr-card-flow">
          <div class="pr-flow-section">
            <span class="pr-flow-tag pr-tag-if">SE</span>
            <div class="pr-flow-fields">
              <div class="pr-field">
                <div class="pr-dropdown" :class="{ 'pr-dropdown-open': openDrop === `campo-${idx}` }" @click.stop>
                  <button class="pr-drop-btn pr-drop-field" @click="toggleDrop(`campo-${idx}`)">
                    <span class="pr-drop-text">{{ campoNome(regra.campo_condicao_id) || 'Selecionar campo...' }}</span>
                    <fa-icon icon="chevron-down" class="pr-drop-arrow" />
                  </button>
                  <div v-if="openDrop === `campo-${idx}`" class="pr-drop-list">
                    <button
                      v-for="c in camposContexto"
                      :key="c.id"
                      class="pr-drop-item"
                      :class="{ 'pr-drop-item-active': regra.campo_condicao_id == c.id }"
                      @click="regra.campo_condicao_id = c.id; openDrop = null"
                    >
                      <span class="pr-drop-item-dot pr-dot-blue"></span>
                      {{ c.nome }}
                    </button>
                  </div>
                </div>
              </div>
              <div class="pr-field">
                <div class="pr-dropdown" :class="{ 'pr-dropdown-open': openDrop === `op-${idx}` }" @click.stop>
                  <button class="pr-drop-btn pr-drop-op" @click="toggleDrop(`op-${idx}`)">
                    <span class="pr-drop-text">{{ operadorLabel(regra.operador) || 'operador...' }}</span>
                    <fa-icon icon="chevron-down" class="pr-drop-arrow" />
                  </button>
                  <div v-if="openDrop === `op-${idx}`" class="pr-drop-list">
                    <button
                      v-for="op in operadores"
                      :key="op.value"
                      class="pr-drop-item"
                      :class="{ 'pr-drop-item-active': regra.operador === op.value }"
                      @click="regra.operador = op.value; openDrop = null"
                    >
                      <span class="pr-drop-item-symbol">{{ op.symbol }}</span>
                      {{ op.label }}
                    </button>
                  </div>
                </div>
              </div>
              <div class="pr-field">
                <input
                  v-model="regra.valor_comparacao"
                  class="pr-inp"
                  placeholder="valor..."
                />
              </div>
            </div>
          </div>

          <div class="pr-flow-connector">
            <fa-icon icon="arrow-down" />
          </div>

          <div class="pr-flow-section">
            <span class="pr-flow-tag pr-tag-then">ENTÃO</span>
            <div class="pr-flow-fields">
              <div class="pr-field pr-field-grow">
                <div class="pr-dropdown" :class="{ 'pr-dropdown-open': openDrop === `var-${idx}` }" @click.stop>
                  <button class="pr-drop-btn pr-drop-var" @click="toggleDrop(`var-${idx}`)">
                    <span class="pr-drop-text">{{ variavelNome(regra.variavel_alvo_id) || 'Selecionar variável...' }}</span>
                    <fa-icon icon="chevron-down" class="pr-drop-arrow" />
                  </button>
                  <div v-if="openDrop === `var-${idx}`" class="pr-drop-list pr-drop-list-lg">
                    <button
                      v-for="v in variaveisNumericas"
                      :key="v.id"
                      class="pr-drop-item"
                      :class="{ 'pr-drop-item-active': regra.variavel_alvo_id == v.id }"
                      @click="regra.variavel_alvo_id = v.id; openDrop = null"
                    >
                      <span class="pr-drop-item-dot pr-dot-green"></span>
                      {{ v.nome }}
                    </button>
                  </div>
                </div>
              </div>
              <span class="pr-flow-eq">=</span>
              <div class="pr-field pr-field-sm">
                <input
                  v-model.number="regra.valor_override"
                  type="number"
                  step="any"
                  class="pr-inp pr-inp-val"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="pr-card-footer">
          <fa-icon icon="edit" class="pr-note-icon" />
          <input
            v-model="regra.descricao"
            class="pr-note-input"
            placeholder="Adicionar nota..."
          />
        </div>
      </div>

      <button class="pr-btn-add pr-btn-add-full" @click="adicionar">
        <fa-icon icon="plus" /> Nova Regra
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "PainelRegras",
  props: {
    regras: { type: Array, default: () => [] },
    camposContexto: { type: Array, default: () => [] },
    variaveis: { type: Array, default: () => [] },
  },
  data() {
    return {
      openDrop: null,
      operadores: [
        { value: "igual", label: "igual a", symbol: "=" },
        { value: "diferente", label: "diferente de", symbol: "\u2260" },
        { value: "contem", label: "contém", symbol: "\u2283" },
        { value: "nao_contem", label: "não contém", symbol: "\u2285" },
        { value: "maior", label: "maior que", symbol: ">" },
        { value: "menor", label: "menor que", symbol: "<" },
        { value: "maior_igual", label: "maior ou igual", symbol: "\u2265" },
        { value: "menor_igual", label: "menor ou igual", symbol: "\u2264" },
        { value: "em", label: "está em", symbol: "\u2208" },
        { value: "nao_em", label: "não está em", symbol: "\u2209" },
      ],
    };
  },
  computed: {
    variaveisNumericas() {
      return this.variaveis.filter((v) => v.tipo !== "contexto");
    },
    duplicatas() {
      const vistas = {};
      const dups = new Set();
      this.regras.forEach((r, i) => {
        if (!r.campo_condicao_id || !r.operador || !r.variavel_alvo_id) return;
        const key = `${r.campo_condicao_id}|${r.operador}|${r.valor_comparacao}|${r.variavel_alvo_id}`;
        if (key in vistas) {
          dups.add(vistas[key]);
          dups.add(i);
        } else {
          vistas[key] = i;
        }
      });
      return dups;
    },
  },
  methods: {
    adicionar() {
      this.$emit("adicionar");
    },
    remover(idx) {
      this.$emit("remover", idx);
    },
    toggleAtiva(idx) {
      this.$emit("toggle-ativa", idx);
    },
    toggleDrop(key) {
      this.openDrop = this.openDrop === key ? null : key;
    },
    fecharDropdowns() {
      this.openDrop = null;
    },
    campoNome(id) {
      const c = this.camposContexto.find((x) => x.id == id);
      return c ? c.nome : "";
    },
    variavelNome(id) {
      const v = this.variaveisNumericas.find((x) => x.id == id);
      return v ? v.nome : "";
    },
    operadorLabel(val) {
      const op = this.operadores.find((x) => x.value === val);
      return op ? `${op.symbol} ${op.label}` : "";
    },
  },
};
</script>

<style scoped>
.pr-root {
  max-width: 640px;
  margin: 32px auto;
  padding: 0 24px;
}

/* HEADER */
.pr-header {
  display: flex; align-items: center; gap: 14px;
  margin-bottom: 28px;
}
.pr-header-icon {
  width: 40px; height: 40px; border-radius: 12px;
  background: linear-gradient(135deg, #6366f1, #818cf8);
  color: #fff; display: flex; align-items: center; justify-content: center;
  font-size: 16px; flex-shrink: 0;
}
.pr-title {
  font-size: 16px; font-weight: 700; color: #1f2937; margin: 0 0 2px;
}
.pr-desc {
  font-size: 12px; color: #9ca3af; margin: 0;
}

/* EMPTY STATE */
.pr-empty {
  text-align: center; padding: 40px 0;
}
.pr-empty-visual {
  display: flex; align-items: center; justify-content: center; gap: 12px;
  margin-bottom: 20px;
}
.pr-empty-pill {
  padding: 6px 18px; border-radius: 20px; font-size: 11px;
  font-weight: 800; letter-spacing: 1px;
}
.pr-pill-if {
  background: linear-gradient(135deg, #dbeafe, #eff6ff); color: #3b82f6;
  border: 1.5px solid #93c5fd;
}
.pr-pill-then {
  background: linear-gradient(135deg, #d1fae5, #ecfdf5); color: #059669;
  border: 1.5px solid #6ee7b7;
}
.pr-empty-arrow { color: #d1d5db; font-size: 18px; }
.pr-empty-text {
  font-size: 14px; font-weight: 600; color: #6b7280; margin: 0 0 4px;
}
.pr-empty-sub {
  font-size: 12px; color: #9ca3af; margin: 0 0 18px;
}

/* ADD BUTTON */
.pr-btn-add {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 20px; border: 2px dashed #c7d2fe; border-radius: 10px;
  background: #fff; color: #6366f1; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.2s;
}
.pr-btn-add:hover {
  border-color: #6366f1; background: #eef2ff;
  box-shadow: 0 2px 8px rgba(99,102,241,0.1);
}
.pr-btn-add-full { margin-top: 14px; width: 100%; justify-content: center; }

/* CARD */
.pr-card {
  background: #fff; border: 1.5px solid #e5e7eb; border-radius: 14px;
  margin-bottom: 14px; overflow: visible; transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.pr-card:hover { border-color: #c7d2fe; box-shadow: 0 4px 12px rgba(99,102,241,0.08); }
.pr-card-off { opacity: 0.45; }
.pr-card-off:hover { opacity: 0.6; }
.pr-card-dup { border-color: #fca5a5 !important; background: #fef2f2; }
.pr-card-dup:hover { border-color: #f87171 !important; }
.pr-dup-warn {
  color: #dc2626; font-size: 10px; margin-left: 8px;
  display: inline-flex; align-items: center; gap: 3px;
}

/* CARD TOP */
.pr-card-top {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 16px;
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  border-bottom: 1px solid #e5e7eb;
  border-radius: 13px 13px 0 0;
}
.pr-card-badge {
  display: flex; align-items: center; gap: 6px;
  font-size: 11px; font-weight: 700; color: #6366f1;
  text-transform: uppercase; letter-spacing: 0.5px;
}
.pr-card-actions { display: flex; gap: 4px; }
.pr-act-btn {
  background: none; border: none; cursor: pointer;
  width: 28px; height: 28px; border-radius: 6px;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; transition: all 0.15s;
}
.pr-act-on { color: #059669; }
.pr-act-on:hover { background: #ecfdf5; }
.pr-act-off { color: #d1d5db; }
.pr-act-off:hover { background: #f3f4f6; color: #6b7280; }
.pr-act-del { color: #d1d5db; }
.pr-act-del:hover { background: #fef2f2; color: #dc2626; }

/* FLOW */
.pr-card-flow { padding: 16px; overflow: visible; }
.pr-flow-section {
  display: flex; align-items: flex-start; gap: 10px;
}
.pr-flow-tag {
  padding: 4px 10px; border-radius: 6px; font-size: 10px;
  font-weight: 800; letter-spacing: 0.8px; flex-shrink: 0; margin-top: 8px;
}
.pr-tag-if {
  background: linear-gradient(135deg, #dbeafe, #eff6ff); color: #3b82f6;
}
.pr-tag-then {
  background: linear-gradient(135deg, #d1fae5, #ecfdf5); color: #059669;
}
.pr-flow-fields {
  display: flex; gap: 8px; flex: 1; align-items: center; flex-wrap: wrap;
}
.pr-field { position: relative; flex: 1 1 140px; min-width: 120px; }
.pr-field-grow { flex: 2 1 180px; }
.pr-field-sm { flex: 0 0 90px; min-width: 90px; }
.pr-flow-connector {
  display: flex; align-items: center; justify-content: center;
  padding: 6px 0; color: #c7d2fe; font-size: 12px;
  margin-left: 36px;
}
.pr-flow-eq {
  display: flex; align-items: center; justify-content: center;
  font-size: 16px; font-weight: 700; color: #6366f1;
  flex-shrink: 0; margin-top: 7px;
}

/* CUSTOM DROPDOWN */
.pr-dropdown { position: relative; }
.pr-drop-btn {
  width: 100%; padding: 9px 32px 9px 12px; text-align: left;
  border: 1.5px solid #e5e7eb; border-radius: 8px;
  font-size: 13px; font-weight: 600; cursor: pointer;
  background: #f9fafb; transition: all 0.15s;
  display: flex; align-items: center; position: relative;
}
.pr-drop-btn:hover { border-color: #c7d2fe; background: #f5f3ff; }
.pr-dropdown-open .pr-drop-btn {
  border-color: #818cf8; background: #fff;
  box-shadow: 0 0 0 3px rgba(99,102,241,0.08);
}
.pr-drop-text {
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1;
}
.pr-drop-arrow {
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  font-size: 10px; color: #9ca3af; transition: transform 0.15s;
}
.pr-dropdown-open .pr-drop-arrow { transform: translateY(-50%) rotate(180deg); color: #6366f1; }
.pr-drop-field { color: #3b82f6; }
.pr-drop-op { color: #6366f1; }
.pr-drop-var { color: #059669; }

.pr-drop-list {
  position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 50;
  background: #fff; border: 1.5px solid #e5e7eb; border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  padding: 4px; max-height: 220px; overflow-y: auto;
}
.pr-drop-list-lg { min-width: 240px; }
.pr-drop-item {
  display: flex; align-items: center; gap: 8px; width: 100%;
  padding: 9px 12px; border: none; background: none; border-radius: 7px;
  font-size: 13px; font-weight: 500; color: #374151; cursor: pointer;
  transition: all 0.1s; text-align: left;
}
.pr-drop-item:hover { background: #f5f3ff; color: #4f46e5; }
.pr-drop-item-active {
  background: #eef2ff; color: #4f46e5; font-weight: 600;
}
.pr-drop-item-dot {
  width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
}
.pr-dot-blue { background: #3b82f6; }
.pr-dot-green { background: #059669; }
.pr-drop-item-symbol {
  width: 20px; text-align: center; font-size: 14px; font-weight: 700;
  color: #6366f1; flex-shrink: 0;
}

/* scrollbar dropdown */
.pr-drop-list::-webkit-scrollbar { width: 5px; }
.pr-drop-list::-webkit-scrollbar-track { background: transparent; }
.pr-drop-list::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }
.pr-drop-list::-webkit-scrollbar-thumb:hover { background: #9ca3af; }

/* INPUTS */
.pr-inp {
  width: 100%; padding: 9px 12px;
  border: 1.5px solid #e5e7eb; border-radius: 8px;
  font-size: 13px; font-weight: 600; color: #374151;
  background: #f9fafb; transition: all 0.15s;
}
.pr-inp:hover { border-color: #c7d2fe; }
.pr-inp:focus {
  outline: none; border-color: #818cf8; background: #fff;
  box-shadow: 0 0 0 3px rgba(99,102,241,0.08);
}
.pr-inp-val {
  text-align: center; font-weight: 700; font-size: 15px; color: #059669;
  background: #ecfdf5; border-color: #a7f3d0;
}
.pr-inp-val:hover { border-color: #34d399; }
.pr-inp-val:focus { background: #fff; border-color: #34d399; box-shadow: 0 0 0 3px rgba(5,150,105,0.08); }

/* FOOTER / NOTE */
.pr-card-footer {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 16px; border-top: 1px solid #f3f4f6;
  background: #fafafa; transition: background 0.15s;
  border-radius: 0 0 13px 13px;
}
.pr-card-footer:hover { background: #f3f4f6; }
.pr-note-icon { font-size: 12px; color: #c7d2fe; flex-shrink: 0; }
.pr-note-input {
  width: 100%; border: none; background: transparent;
  font-size: 12px; color: #6b7280; outline: none;
  font-weight: 500;
}
.pr-note-input:focus { color: #374151; }
.pr-note-input::placeholder { color: #c7d2fe; font-style: italic; font-weight: 400; }
</style>
